import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { db } from '../lib/db'
import 'remixicon/fonts/remixicon.css'

async function hashPassword(password) {
  const encoder = new TextEncoder()
  const data = encoder.encode(password)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

export default function ProfileModal({ isOpen, onClose }) {
  const [view, setView] = useState('login')
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  })

  useEffect(() => {
    const savedProfile = localStorage.getItem('customer_profile')
    if (savedProfile) {
      setProfile(JSON.parse(savedProfile))
      setView('profile')
    }
  }, [isOpen])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setError('')
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const passwordHash = await hashPassword(formData.password)
      const user = await db.loginCustomer(formData.email, passwordHash)
      if (user) {
        setProfile(user)
        localStorage.setItem('customer_profile', JSON.stringify(user))
        setView('profile')
        setSuccess('Welcome back!')
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError('Invalid email or password.')
      }
    } catch (err) {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSignup = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.')
      setLoading(false)
      return
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.')
      setLoading(false)
      return
    }

    try {
      const passwordHash = await hashPassword(formData.password)
      const newProfile = await db.createCustomerProfile({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        password_hash: passwordHash
      })
      setProfile(newProfile)
      localStorage.setItem('customer_profile', JSON.stringify(newProfile))
      setView('profile')
      setSuccess('Account created successfully!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      if (err.message?.includes('unique') || err.message?.includes('duplicate')) {
        setError('An account with this email already exists.')
      } else {
        setError('Something went wrong. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const updated = await db.updateCustomerProfile(profile.id, {
        name: formData.name || profile.name,
        phone: formData.phone || profile.phone,
        address: formData.address || profile.address
      })
      setProfile(updated)
      localStorage.setItem('customer_profile', JSON.stringify(updated))
      setSuccess('Profile updated!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Failed to update profile.')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters.')
      setLoading(false)
      return
    }
    if (formData.newPassword !== formData.confirmNewPassword) {
      setError('New passwords do not match.')
      setLoading(false)
      return
    }

    try {
      const oldHash = await hashPassword(formData.oldPassword)
      const newHash = await hashPassword(formData.newPassword)
      const result = await db.changeCustomerPassword(profile.id, oldHash, newHash)
      if (result) {
        setSuccess('Password changed successfully!')
        setFormData({ ...formData, oldPassword: '', newPassword: '', confirmNewPassword: '' })
        setView('profile')
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError('Current password is incorrect.')
      }
    } catch (err) {
      setError('Failed to change password.')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    setProfile(null)
    localStorage.removeItem('customer_profile')
    setFormData({ name: '', email: '', password: '', confirmPassword: '', phone: '', address: '', oldPassword: '', newPassword: '', confirmNewPassword: '' })
    setView('login')
  }

  if (!isOpen) return null

  const inputClass = "w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-green-600 bg-clip-text text-transparent">
              {view === 'profile' ? 'My Profile' : view === 'login' ? 'Sign In' : view === 'signup' ? 'Create Account' : 'Change Password'}
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm">
              {success}
            </div>
          )}

          {/* Login View */}
          {view === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="your@email.com" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} name="password" required value={formData.password} onChange={handleChange} placeholder="Enter your password" className={inputClass} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold text-lg disabled:opacity-50">
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <p className="text-center text-sm text-gray-600">
                Don't have an account?{' '}
                <button type="button" onClick={() => { setView('signup'); setError('') }} className="text-orange-600 font-semibold hover:underline">
                  Sign Up
                </button>
              </p>
            </form>
          )}

          {/* Signup View */}
          {view === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input type="text" name="name" required value={formData.name} onChange={handleChange} placeholder="Your full name" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="your@email.com" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} name="password" required value={formData.password} onChange={handleChange} placeholder="Min. 6 characters" className={inputClass} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                <input type="password" name="confirmPassword" required value={formData.confirmPassword} onChange={handleChange} placeholder="Confirm your password" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+263 XXX XXX XXX" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange} placeholder="Your delivery address" className={inputClass} />
              </div>
              <button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold text-lg disabled:opacity-50">
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
              <p className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <button type="button" onClick={() => { setView('login'); setError('') }} className="text-orange-600 font-semibold hover:underline">
                  Sign In
                </button>
              </p>
            </form>
          )}

          {/* Profile View */}
          {view === 'profile' && profile && (
            <div className="space-y-6">
              <div className="flex items-center gap-4 p-4 bg-gradient-to-br from-orange-50 to-green-50 rounded-2xl">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-green-500 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-3xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">{profile.name}</h3>
                  <p className="text-sm text-gray-600">{profile.email}</p>
                </div>
              </div>

              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" name="name" defaultValue={profile.name} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" name="phone" defaultValue={profile.phone || ''} onChange={handleChange} placeholder="+263 XXX XXX XXX" className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
                  <input type="text" name="address" defaultValue={profile.address || ''} onChange={handleChange} placeholder="Your delivery address" className={inputClass} />
                </div>
                <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold disabled:opacity-50">
                  {loading ? 'Saving...' : 'Update Profile'}
                </button>
              </form>

              <button
                onClick={() => { setView('changePassword'); setError(''); setSuccess('') }}
                className="w-full py-3 border-2 border-orange-300 text-orange-600 rounded-xl font-medium hover:bg-orange-50 transition-colors"
              >
                Change Password
              </button>

              <button
                onClick={handleLogout}
                className="w-full py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                Sign Out
              </button>
            </div>
          )}

          {/* Change Password View */}
          {view === 'changePassword' && (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input type="password" name="oldPassword" required value={formData.oldPassword} onChange={handleChange} placeholder="Enter current password" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input type="password" name="newPassword" required value={formData.newPassword} onChange={handleChange} placeholder="Min. 6 characters" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input type="password" name="confirmNewPassword" required value={formData.confirmNewPassword} onChange={handleChange} placeholder="Confirm new password" className={inputClass} />
              </div>
              <button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold text-lg disabled:opacity-50">
                {loading ? 'Changing...' : 'Change Password'}
              </button>
              <button type="button" onClick={() => { setView('profile'); setError('') }} className="w-full py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors">
                Back to Profile
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
